import { Button, Typography, useMediaQuery, ArrowRightIcon, ArrowLeftIcon } from '@ag1/ui';
import { StudioBaseImageV2 } from '@portal/shared/studio/StudioBaseImage/StudioBaseImage';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { IngredientProductSelectorConfig } from 'types/studio/studio';
import styles from './ProductSelector.module.scss';

const PRODUCTS_PER_VIEW = 4;
const MIN_SCROLL_THRESHOLD = 1;
const PERCENTAGE_MULTIPLIER = 100;
const INDICATOR_OFFSET_XS = 35;
const INDICATOR_OFFSET_SM = 10;
const DESKTOP_ITEM_WIDTH = 132;
const MOBILE_ITEM_WIDTH = 96;
const ITEM_GAP = 8;
const ITEMS_TO_SCROLL = 2;

type ProductSelectorProps = {
  products: IngredientProductSelectorConfig[];
  selectedProductHandle: string | null;
  onSelectProduct: (handle: string | null) => void;
};

export const ProductSelector: React.FC<ProductSelectorProps> = ({
  products,
  selectedProductHandle,
  onSelectProduct
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [indicatorWidth, setIndicatorWidth] = useState(0);
  const [indicatorTranslate, setIndicatorTranslate] = useState(0);
  const isSmOrSmaller = !useMediaQuery('sm');
  const isXsOrSmaller = !useMediaQuery('xs');

  const showCarousel = products.length > PRODUCTS_PER_VIEW;

  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) {
      const { scrollWidth, clientWidth, scrollLeft } = el;
      const maxScrollLeft = scrollWidth - clientWidth;

      const canScroll = maxScrollLeft > 0;
      setCanScrollLeft(canScroll && scrollLeft > MIN_SCROLL_THRESHOLD);
      const currentCanScrollRight = canScroll && maxScrollLeft - scrollLeft > MIN_SCROLL_THRESHOLD;
      setCanScrollRight(currentCanScrollRight);

      const rawWidth = (clientWidth / scrollWidth) * PERCENTAGE_MULTIPLIER;
      const roundedWidth = parseFloat(
        (rawWidth > PERCENTAGE_MULTIPLIER ? PERCENTAGE_MULTIPLIER : rawWidth).toFixed(2)
      );

      let roundedTranslate = 0;
      if (canScroll) {
        const maxTranslateBase = PERCENTAGE_MULTIPLIER - roundedWidth;
        if (currentCanScrollRight) {
          const scrollRatio = scrollLeft / maxScrollLeft;
          const rawTranslate = scrollRatio * maxTranslateBase;
          roundedTranslate = parseFloat(rawTranslate.toFixed(2));
        } else if (scrollLeft > MIN_SCROLL_THRESHOLD) {
          let offset = 0;
          if (isSmOrSmaller) {
            offset = isXsOrSmaller ? INDICATOR_OFFSET_XS : INDICATOR_OFFSET_SM;
          }
          const finalMaxTranslate = maxTranslateBase + offset;
          roundedTranslate = finalMaxTranslate;
        }
      }

      setIndicatorWidth(roundedWidth);
      setIndicatorTranslate(parseFloat(roundedTranslate.toFixed(2)));
    }
  }, [isSmOrSmaller, isXsOrSmaller]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (el && showCarousel) {
      updateScrollState();

      el.addEventListener('scroll', updateScrollState);

      const resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(el);

      return () => {
        el.removeEventListener('scroll', updateScrollState);
        resizeObserver.unobserve(el);
      };
    }
    return undefined;
  }, [products.length, showCarousel, updateScrollState]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollContainerRef.current;
    if (el) {
      const itemWidth = isSmOrSmaller ? MOBILE_ITEM_WIDTH : DESKTOP_ITEM_WIDTH;
      const scrollAmount = (itemWidth + ITEM_GAP) * ITEMS_TO_SCROLL;
      const newScrollLeft = el.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      el.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.scrollWrapper} ref={scrollContainerRef}>
        <div className={styles.productSelectorContainer}>
          {products.map((product) => {
            const baseImageData = product.image?.image;
            const isSelected = selectedProductHandle === product.productHandle;
            const mobileSize = isSelected ? 108 : MOBILE_ITEM_WIDTH; // 108 if selected, 96 otherwise
            const desktopSize = DESKTOP_ITEM_WIDTH; // 132
            const imageSize = isSmOrSmaller ? mobileSize : desktopSize;

            return (
              <button
                key={product.productHandle}
                type="button"
                className={styles.productSelectItem}
                onClick={() => onSelectProduct(product.productHandle ?? null)}>
                {baseImageData?.asset && (
                  <div
                    className={clsx(
                      styles.productImageWrapper,
                      selectedProductHandle === product.productHandle && styles.selected
                    )}>
                    <StudioBaseImageV2 image={baseImageData} sizes={`${imageSize}px`} decorative />
                  </div>
                )}
                <Typography
                  variant="portalText"
                  size="1"
                  component="span"
                  className={styles.productTitle}
                  color={
                    selectedProductHandle === product.productHandle ? 'black-new' : 'grey-1-new'
                  }
                  weight={selectedProductHandle === product.productHandle ? '700' : '400'}>
                  {product.title}
                </Typography>
              </button>
            );
          })}
        </div>
      </div>
      {showCarousel && (
        <div className={styles.controlsContainer}>
          <div className={styles.progressBarContainer}>
            <div className={styles.progressBarTrack}>
              <div
                className={clsx(styles.progressBarThumb, isSmOrSmaller && styles.slowTransition)}
                style={{
                  width: `${indicatorWidth}%`,
                  transform: `translateX(${indicatorTranslate}%)`
                }}
              />
            </div>
          </div>

          <div className={styles.arrowButtonsWrapper}>
            <Button
              type="button"
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              aria-label="Scroll products left"
              variant="outline"
              size="small"
              rounded
              onlyIcon
              icon={<ArrowLeftIcon />}
            />

            <Button
              type="button"
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              aria-label="Scroll products right"
              variant="outline"
              size="small"
              rounded
              onlyIcon
              icon={<ArrowRightIcon />}
            />
          </div>
        </div>
      )}
    </div>
  );
};
